import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, finalize, Observable, of, throwError } from 'rxjs'
import { catchError, delay, filter, switchMap, take, tap } from 'rxjs/operators'
import { AuthService, EventBusService, TokenService } from '../services'
import { ToastrService } from 'ngx-toastr'

/**
 * Authentication HTTP interceptor that will:
 *
 * 1. Set authorization HTTP header before making a request.
 * 2. Update expired access token and retry failed request.
 * https://www.intertech.com/angular-4-tutorial-handling-refresh-token-with-new-httpinterceptor/
 */
@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  isRefreshingToken: boolean = false
  tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)
  tokenObservable: Observable<string | null> = this.tokenSubject.asObservable()
  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    private eventBusService: EventBusService,
    private toastrService: ToastrService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.tokenService.getAccessToken() ?? ''
    if (req.headers.has('ignoreInterceptor')) {
      req.headers.delete('ignoreInterceptor')
      return next.handle(this.addToken(req, accessToken))
    }
    return next.handle(this.addToken(req, accessToken)).pipe(
      switchMap((event) => {
        let needToAuthenticate = false
        if (
          event.type === HttpEventType.Response &&
          event.status === 200 &&
          event.body &&
          Array.isArray(event.body.errors)
        ) {
          const errors = event.body.errors as any[]
          needToAuthenticate = !!errors.find((e) => e.extensions && e.extensions.code === 'UNAUTHENTICATED')
          if (needToAuthenticate) {
            return this.handle401Error(req, next)
          }
        }
        return of(event)
      }),
      catchError((error) => this.handleHttpErrorResponse(error, req, next)),
    )
  }

  private handleHttpErrorResponse(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (error instanceof HttpErrorResponse) {
      switch ((<HttpErrorResponse>error).status) {
        case 401:
          return this.handle401Error(req, next)
        case 403:
          return this.handle403Error(error)
        default:
          console.log("This shouldn't happen - could be happening if backend is down")
          return throwError(error)
      }
    } else {
      return throwError(error)
    }
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (req.url.includes('refresh')) {
      this.toastrService.info('Your login has expired! Please log back in to continue using the app.', 'Login expired!')
      this.logoutUser(true)
    }
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null)
      const refreshToken = this.tokenService.getRefreshToken() ?? ''
      return this.authService.refreshToken(refreshToken).pipe(
        switchMap((tokens: any) => {
          const newToken = tokens.accessToken
          if (newToken) {
            this.tokenSubject.next(newToken)
            return next.handle(this.addToken(req, newToken))
          }

          // If we don't get a new token, we are in trouble so logout.
          return this.logoutUser(true)
        }),
        catchError((error) => {
          if (error.error === 'No token') {
            return throwError(this.createCustomError())
          } else {
            return this.logoutUser(true)
          }
        }),
        finalize(() => {
          this.isRefreshingToken = false
        }),
      )
    }

    return this.tokenObservable.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        if (token != null) {
          return next.handle(this.addToken(req, token ?? ''))
        } else {
          return throwError({
            error: 'Access denied!',
            message: 'Access denied!',
          })
        }
      }),
    )
  }

  handle403Error(error: any) {
    if (error && error.status === 403) {
      this.toastrService.error('Your login information is invalid!', 'Invalid login!')
      return this.logoutUser(true)
    }

    return throwError(error)
  }

  private addToken(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (req.url.includes('refresh')) {
      return req
    }
    if (!token) {
      return req
    }
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } }) as HttpRequest<any>
  }

  logoutUser(onlyFrontend = false) {
    this.eventBusService.emit({
      name: 'logout',
      value: {
        onlyFrontend,
      },
    })
    return throwError(this.createCustomError())
  }

  createCustomError() {
    return {
      error: 'Access denied!',
      message: 'Access denied!',
    }
  }
}
